import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Container, Typography,
  Avatar, IconButton, Tooltip, Skeleton
} from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import DashboardBreadcrumbs from '../dashboard/DashboardBreadcrumbs';

/**
 * Common layout wrapper for all admin back-office pages.
 *
 * Props:
 *  title      – optional page title rendered below breadcrumbs
 *  subtitle   – optional subtitle rendered below title
 *  maxWidth   – MUI Container breakpoint (default 'xl')
 *  fullWidth  – when true, skips the Container so children span the full viewport
 *               width (needed for pages that use SectionBlock or full-width sections)
 */
const AdminPageLayout = ({
  children,
  title,
  subtitle,
  maxWidth = 'xl',
  fullWidth = false,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* ── Sticky admin AppBar ──────────────────────────────────── */}
      <AppBar position="sticky" color="inherit" elevation={1} sx={{ bgcolor: 'white' }}>
        <Toolbar sx={{ gap: 2 }}>

          {/* Logo + company name → Dashboard */}
          <Box
            onClick={() => navigate('/dashboard')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              cursor: 'pointer', flexShrink: 0, userSelect: 'none',
            }}
          >
            {tenantLoading ? (
              <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: 1 }} />
            ) : tenant?.logoUrl ? (
              <Box
                component="img"
                src={tenant.logoUrl}
                alt={tenant.companyName || 'logo'}
                sx={{ height: 36, borderRadius: 1, objectFit: 'contain' }}
              />
            ) : null}

            {tenantLoading ? (
              <Skeleton width={140} height={24} />
            ) : (
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
                {tenant?.companyName || 'Back Office'}
              </Typography>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Logged-in admin — avatar/name open My Profile (account + password) */}
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="My profile">
              <Box
                onClick={() => navigate('/profile')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                  borderRadius: 1, px: 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar
                  sx={{
                    width: 32, height: 32,
                    bgcolor: 'primary.main', color: 'primary.contrastText',
                    fontSize: 13, fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton onClick={() => logout(navigate)} size="small">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

        </Toolbar>
      </AppBar>

      {/* ── Page content ─────────────────────────────────────────── */}
      {fullWidth ? (
        <>
          <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2 }}>
            <DashboardBreadcrumbs currentSection={title} />
          </Box>
          {children}
        </>
      ) : (
        <Container maxWidth={maxWidth} sx={{ py: 3, px: { xs: 2, sm: 4 } }}>
          <DashboardBreadcrumbs currentSection={title} />
          {(title || subtitle) && (
            <Box mb={3}>
              {title && (
                <Typography variant="h4" component="h1" fontWeight={600} color="text.primary"
                  sx={{ mb: subtitle ? 0.5 : 0 }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body1" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          {children}
        </Container>
      )}
    </Box>
  );
};

export default AdminPageLayout;
